import { AssignedTo, AssignmentUserBasicsDto } from "@green0meter/api-client";
import React, { FC, ReactNode, useMemo, useState } from "react";
import { ListItemText, useTheme } from "@mui/material";
import { isEmpty } from "lodash";

import {
  LABEL_ASSIGNMENT_DROPDOWN_OPTIONS,
  DropdownItem,
} from "@components/main/questions/QuestionsAssignment/QuestionsAssignments.constants";
import { useGetAssignableUsers } from "@api";
import { useUserStore } from "@store/user";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Checkbox,
  Divider,
  Chip,
  Stack,
} from "@components/base";

interface AssignmentDropdown {
  assignment: {
    assignedTo?: AssignedTo;
    assignedUsers?: AssignmentUserBasicsDto[];
  };
  handleChange: (item: {
    type: "general" | "user" | "users";
    value: string | AssignedTo | string[];
  }) => void;
  onOpen?: () => void;
  disabled?: boolean;
  usersSelectionEnabled?: boolean;
  companyIds?: string[];
}

const AssignmentDropdown: FC<AssignmentDropdown> = ({
  assignment,
  handleChange,
  onOpen,
  disabled,
  usersSelectionEnabled = false,
  companyIds,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { palette } = useTheme();
  const open = Boolean(anchorEl);
  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
    onOpen?.();
  };
  const handleClose = (): void => {
    setAnchorEl(null);
  };
  const { user } = useUserStore();
  const { assignableUsers } = useGetAssignableUsers(
    user,
    {
      companyIds: companyIds ?? [],
    },
    usersSelectionEnabled,
  );

  const items = useMemo(() => {
    const defaultItems = LABEL_ASSIGNMENT_DROPDOWN_OPTIONS.filter(
      (option) => option.value !== AssignedTo.Mixed,
    ).map((option) => ({
      label: option.label,
      value: option.value,
      type: "general",
    }));

    if (!assignableUsers) {
      return defaultItems;
    }

    return [
      ...defaultItems,
      ...assignableUsers.map((u) => ({
        label: u.fullName,
        value: u.id,
        type: "user",
      })),
    ];
  }, [assignableUsers]);

  const handleAssignmentChange = (item: {
    label: ReactNode;
    value: string | AssignedTo;
    type: string;
  }): void => {
    if (
      [
        AssignedTo.Everyone,
        AssignedTo.Hidden,
        AssignedTo.OnlyOwner,
        AssignedTo.OwnerAndManagers,
      ].includes(item.value as AssignedTo)
    ) {
      handleChange({ type: "general", value: item.value as AssignedTo });
      handleClose();
    }
  };

  const handleUserToggle = (userId: string): void => {
    const currentUserIds = assignment.assignedUsers?.map((u) => u.id) ?? [];
    const newUserIds = currentUserIds.includes(userId)
      ? currentUserIds.filter((id) => id !== userId)
      : [...currentUserIds, userId];

    if (isEmpty(newUserIds)) {
      // If no users are selected, default back to "Only Owner" without any users
      handleChange({ type: "general", value: AssignedTo.OnlyOwner });
    } else {
      // Otherwise, update the assigned users
      handleChange({ type: "users", value: newUserIds });
    }
  };

  const generalItems = items.filter((item) => item.type === "general");
  const userItems = items.filter((item) => item.type === "user");
  const selectedUserIds = assignment.assignedUsers?.map((u) => u.id) ?? [];

  const getButtonLabel = (): ReactNode => {
    if (assignment.assignedTo === AssignedTo.UserSpecific) {
      if (assignment.assignedUsers && assignment.assignedUsers.length > 0) {
        return (
          <Stack direction="row" spacing={1}>
            {assignment.assignedUsers.map((user) => (
              <Chip key={user.id} label={user.fullName} color="info" />
            ))}
          </Stack>
        );
      }

      return (
        <DropdownItem
          label={"assignment.userSpecific"}
          color={palette.custom.blue[100]}
          bgColor={palette.custom.blue[50]}
          borderColor={palette.custom.blue[200]}
        />
      );
    }

    return (
      LABEL_ASSIGNMENT_DROPDOWN_OPTIONS.find(
        (o) => o.value === assignment.assignedTo,
      )?.label ?? ""
    );
  };

  return (
    <Box>
      <Button
        variant="text"
        onClick={(e) => {
          handleOpen(e);
        }}
        disabled={disabled}
        size="extraSmall"
        color="inherit"
        sx={{ opacity: disabled ? 0.5 : 1 }}
      >
        {getButtonLabel()}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => {
          handleClose();
        }}
      >
        {generalItems.map((item, index) => (
          <MenuItem
            key={index}
            onClick={() => {
              handleAssignmentChange(item);
            }}
          >
            {item.label}
          </MenuItem>
        ))}
        {usersSelectionEnabled && userItems.length > 0 && (
          <>
            <Divider />
            {userItems.map((item, index) => {
              const isChecked = selectedUserIds.includes(item.value as string);

              return (
                <MenuItem
                  key={`user-${index}`}
                  onClick={() => {
                    handleUserToggle(item.value as string);
                  }}
                  sx={{ py: 0.5 }}
                >
                  <Checkbox checked={isChecked} size="small" />
                  <ListItemText primary={item.label} />
                </MenuItem>
              );
            })}
          </>
        )}
      </Menu>
    </Box>
  );
};

export default AssignmentDropdown;
